<template>
    <div
        :style="cssVars"
        :class="{'loading': loading}"
    >
        <no-data-animation v-if="loading" />
        <div
            v-else
            style="width: 100%"
        >
            <div v-if="isMultiQueueRulesDashboard(data)">
                <queue-rules-table
                    :data="data"
                    :queue-rules-data="queuesTableData"
                    @on-update="onUpdate"
                />
            </div>
            <div v-if="isMultiQueuesDashboard(data)">
                <queues-table
                    :data="data"
                    :queues-data="queuesTableData"
                    @on-update="onUpdate"
                />
            </div>
            <div v-if="isRealtimeWidget(data)">
                <RealTimeUserTable
                    :columns="columns"
                    :data="data"
                    :searchable-fields="searchableFields"
                    :table-data="tableData"
                    @on-update="onUpdate"
                />
            </div>
            <div v-if="isSocketsRealTimeTableWidget(data)">
                <SocketsRealTimeTable
                    :columns="columns"
                    :data="data"
                    :searchable-fields="searchableFields"
                    :table-data="tableData"
                    @on-update="onUpdate"
                />
            </div>
            <div v-if="isSimpleTable">
                <data-table
                    ref="dataTableRef"
                    v-bind="$attrs"
                    :widget="data"
                    :border="border"
                    :columns="availableColumns"
                    :editable="editable"
                    :show-columns="visibleColumns"
                    :stripe="stripe"
                    :all-records="tableData"
                    :table-data="fetchTableData"
                    :widget-title="data.Title"
                    :default-sort="defaultSorting"
                    can-sort-rows
                    :columns-with-percentage="columnsWithPercentage"
                    @sort-change="onSortChange"
                    @on-update-layout="onUpdateLayout"
                >
                    <template #Recording="{row}">
                        <a
                            v-if="row.IVR_unique_id"
                            :href="buildRecordingUrl(row)"
                            target="_blank"
                        >
                            <i class="vc-icon-info icon-lg text-primary cursor-pointer" />
                        </a>
                        <div v-else>
                            {{ $t('general.NA') }}
                        </div>
                    </template>
                    <template #pagination-rows-per-page>
                        <div class="flex items-center">
                            <el-select
                                v-model="pageSize"
                                class="w-48 mx-4 flex items-center"
                                size="small"
                                @change="handlePageChange(1)"
                            >
                                <el-option
                                    v-for="option in pageSizes"
                                    :key="option"
                                    :value="parseInt(option)"
                                    :label="`${option} ${$t('general.perpage')}`"
                                />
                                <slot>
                                    <div class="w-40 mx-2 pb-1">
                                        <span class="text-xs flex justify-center pb-2 main-text-color">{{
                                            $t('widget.table.customValue')
                                        }}</span>
                                        <div class="flex flex-row">
                                            <el-input
                                                v-model="customPageSize"
                                                size="mini"
                                                class="mx-1"
                                            />
                                            <el-button
                                                size="mini"
                                                class="mx-1"
                                                @click="applyCustomPageSize"
                                            >
                                                {{ $t('general.apply') }}
                                            </el-button>
                                        </div>
                                    </div>
                                </slot>
                            </el-select>
                        </div>
                    </template>
                    <template #pagination>
                        <div class="flex items-center">
                            <Pagination
                                v-model="currentPage"
                                class="z-10 rounded-b-md"
                                :per-page="pageSize"
                                hide-per-page-option
                                :total="filteredDataLength"
                            />
                        </div>
                    </template>
                    <template #search-input>
                        <div class="flex items-center w-48 px-2">
                            <el-input
                                v-model="filter"
                                clearable
                                :placeholder="$t('general.search')"
                                size="small"
                            >
                                <template #prefix>
                                    <i class="el-input__icon vc-icon-search icon-md text-primary ml-1" />
                                </template>
                            </el-input>
                        </div>
                    </template>
                </data-table>
            </div>
        </div>
    </div>
</template>
<script>
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import startCase from 'lodash/startCase'
import { Option, Select } from 'element-ui'
import {
    getDefaultTimeDelay,
    isMultiQueueRulesDashboard,
    isMultiQueuesDashboard,
    isRealtimeWidget,
    isSocketsRealTimeTableWidget
} from '@/helpers/widgetUtils'
import MultiQueuesDashboard from '@/components/Widgets/Data/Queue/MultiQueuesDashboard.vue'
import MultiQueueRulesDashboard from '@/components/Widgets/Data/Queue/MultiQueueRulesDashboard.vue'
import dataTableMixin from '@/mixins/dataTableMixin'
import { getDynamicColumns } from '@/enum/realTimeTableConfigs'
import { defaultDialerTableColumns } from '@/enum/dialerRealTimeTableConfigs'
import isEqual from 'lodash/isEqual'
import { getTranslationOrUndefined } from '@/helpers/translation'
import requestMixin from '@/mixins/requestMixin'
import getDataIntervalMixin from '@/mixins/getDataIntervalMixin'
import env from '@/env'
import { widgetTableContentFontSize, widgetTableHeaderFontSize } from '@/composables/globalData/useLayouts'
import { DEFAULT_FONT_SIZE } from '@/enum/default-layout-config'
import { sortTableDataByDefaultList } from '@/helpers/tableSortingUtils'

export default {
    mixins: [ dataTableMixin, requestMixin, getDataIntervalMixin ],
    components: {
        DataTable: () => import('@/components/Table/DataTable.vue'),
        // AudioPlayer: () => import('@/components/Audio/AudioPlayer.vue'),
        RealTimeUserTable: () => import('./RealTimeUserTable.vue'),
        Pagination: () => import('@/modules/common/components/Pagination.vue'),
        [Select.name]: Select,
        [Option.name]: Option,
        [MultiQueuesDashboard.name]: MultiQueuesDashboard,
        [MultiQueueRulesDashboard.name]: MultiQueueRulesDashboard,
        SocketsRealTimeTable: () => import('./SocketsRealTimeTable.vue'),
        NoDataAnimation: () => import('@/components/DataLoadAnimation.vue')
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
        editable: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            tableData: [],
            columns: [],
            searchableFields: [],
            pageSizes: [ 5, 10, 25, 50, 100, 500 ],
            pageSize: 10,
            customPageSize: 10,
            pagerCount: 5,
            currentPage: 1,
            filter: '',
            filteredDataLength: null,
            hideOnSinglePage: true,
            border: true,
            widget: cloneDeep(this.data),
            stripe: false,
            queuesTableData: [],
            columnsWithPercentage: [],
            fetchTableData: [],
            loading: true
        }
    },
    computed: {
        // Get dynamic columns with translated labels at runtime
        dynamicColumns () {
            return getDynamicColumns()
        },
        isSimpleTable () {
            return !this.isMultiQueueRulesDashboard(this.data) && !this.isMultiQueuesDashboard(this.data) && !this.isRealtimeWidget(this.data) && !this.isSocketsRealTimeTableWidget(this.data)
        },
        columnsAreManaged () {
            return !!get(this.data.WidgetLayout, 'Columns.visibleColumns')
        },
        layoutFontSize () {
            return widgetTableContentFontSize.value.fontSize
        },
        tableHeaderFontSize () {
            return widgetTableHeaderFontSize.value.fontSize
        },
        dynamicFontSize () {
            return get(this.data.WidgetLayout, 'tableContentFontSize', this.layoutFontSize)
        },
        dynamicHeaderFontSize () {
            return get(this.data.WidgetLayout, 'tableHeaderFontSize', this.tableHeaderFontSize)
        },
        cssVars () {
            const replacePxInString = (string) => {
                return string.replace('px', '')
            }
            const defaultFontSizeNumber = DEFAULT_FONT_SIZE
            const dynamicFontSizeNumber = replacePxInString(this.dynamicFontSize)
            const fontSize = dynamicFontSizeNumber >= defaultFontSizeNumber ? dynamicFontSizeNumber : defaultFontSizeNumber

            return {
                '--dynamic-font-size': fontSize + 'px',
                '--dynamic-header-font-size': this.dynamicHeaderFontSize
            }
        },
        defaultSorting () {
            const { sortingParams } = this.data.WidgetLayout
            return sortingParams || {}
        },
    },
    methods: {
        isMultiQueuesDashboard,
        isMultiQueueRulesDashboard,
        isRealtimeWidget,
        isSocketsRealTimeTableWidget,
        buildRecordingUrl (row) {
            return `${env.VITE_APP_CDR_INFO_URL}?id=${row.IVR_unique_id}&tab=ai_data`
        },
        async getWidgetData () {
            try {
                const data = await this.loadWidgetData(this.widget)

                if (this.isSocketsRealTimeTableWidget(this.widget)) {
                    this.tableData = defaultDialerTableColumns
                    this.columns = defaultDialerTableColumns
                    this.searchableFields = defaultDialerTableColumns.map(el => el.prop)
                }

                if (!data) {
                    return
                }

                if (isMultiQueuesDashboard(this.widget) || isMultiQueueRulesDashboard(this.widget)) {
                    this.queuesTableData = data
                    return
                }

                const columns = []
                const containsDate = false
                const percentageColumns = []
                const uniqColumns = []

                if (this.widget.WidgetTemplateColumns.length) {
                    this.widget.WidgetTemplateColumns.forEach(column => {
                        if (!uniqColumns.find(el => el.WidgetTemplateColumnsName === column.WidgetTemplateColumnsName)) {
                            uniqColumns.push(column)
                        }
                    })

                    uniqColumns.forEach((el) => {
                        const label = getTranslationOrUndefined(el.TemplateNameContentTag)
                            || getTranslationOrUndefined(el.WidgetTemplateColumnsName)
                            || startCase(el.WidgetTemplateColumnsName)

                        const columnData = {
                            prop: el.WidgetTemplateColumnsName,
                            fixed: false,
                            align: 'center',
                            label,
                            className: containsDate ? 'direction-ltr' : ''
                        }
                        if ('ConfData' in el && el.ConfData.DefaultWidth) {
                            columnData.minWidth = el.ConfData.DefaultWidth
                        } else {
                            columnData.minWidth = 300
                        }

                        columns.push(columnData)

                        this.searchableFields.push(el.WidgetTemplateColumnsName)
                    })
                } else if (data.length) {
                    const firstRecord = data[0]
                    for (const column in firstRecord) {
                        const label = getTranslationOrUndefined(`WidgetTemplateColumnName.${column}`)
                            || getTranslationOrUndefined(column)
                            || startCase(column)

                        const columnData = {
                            prop: column,
                            fixed: false,
                            align: 'center',
                            label,
                            className: containsDate ? 'direction-ltr' : '',
                            minWidth: 300
                        }

                        columns.push(columnData)

                        if (column === 'Recording') {
                            columnData.minWidth = '320px'
                        }

                        this.searchableFields.push(column)
                    }
                }

                if (isRealtimeWidget(this.data) && !this.columnsAreManaged) {
                    const dynamicColumnsFilteredByPresent = this.dynamicColumns.filter(({ prop }) => {
                        return !columns.find(({ prop: columnProp }) => columnProp === prop)
                    })

                    for (let i = 0; i < dynamicColumnsFilteredByPresent.length; i++) {
                        columns.splice(3 + i, 0, dynamicColumnsFilteredByPresent[i])
                    }
                }

                this.tableData = data
                this.columns = columns
                this.columnsWithPercentage = percentageColumns
            } catch (e) {
                const status = get(e, 'response.status')
                if (status === 400) {
                    const refreshDelay = getDefaultTimeDelay()
                    this.$set(this.data, 'DefaultRefreshInterval', refreshDelay)
                }
            } finally {
                this.loading = false
            }
        },
        getRecordingUrl (recordingLink) {
            const div = document.createElement('div')
            div.innerHTML = recordingLink
            const anchor = div.querySelector('a')
            if (anchor && anchor.href) {
                return anchor.href
            }
            return ''
        },
        storePaginationSettings (pageSize) {
            this.data.WidgetLayout['paginationSize'] = Number(pageSize)
            this.$emit('on-update', this.data)
            this.applyPaginationSettings()
            this.fetchTableData = this.updatePaginatedData(this.tableData)
        },
        onUpdate (data, silence) {
            this.$emit('on-update', data, silence)
        },
        applyPaginationSettings () {
            this.pageSize = get(this.data.WidgetLayout, 'paginationSize', 10)
            this.customPageSize = this.pageSize
            if (this.$refs['dataTableRef']) {
                this.$refs['dataTableRef'].clearDataSort()
            }
        },
        applyCustomPageSize () {
            const pageSize = this.customPageSize
            this.storePaginationSettings(pageSize)
        },
        updatePaginatedData (data) {
            let tableData = data
            
            tableData = sortTableDataByDefaultList(tableData, this.data)

            if (this.filter) {
                tableData = tableData.filter(c => {
                    return this.searchableFields.some(field => {
                        if (c[field]) {
                            return c[field].toString().toLowerCase().includes(this.filter.toLowerCase())
                        }
                        return false
                    })
                })
            }
            this.filteredDataLength = tableData.length
            return tableData.slice(this.pageSize * (this.currentPage - 1), this.pageSize * this.currentPage)
        },
        onSortChange ({ prop, order }) {
            const { sortingParams } = this.data.WidgetLayout
            if (sortingParams) {
                sortingParams.order = order
                sortingParams.prop = prop
            } else {
                this.data.WidgetLayout.sortingParams = {
                    order: order,
                    prop: prop
                }
            }
            this.onUpdate(this.data, true)
        },
    },
    watch: {
        filter () {
            if (this.currentPage === 1) {
                this.fetchTableData = this.updatePaginatedData(this.tableData)
            }

            this.currentPage = 1
        },
        data: {
            immediate: true,
            deep: true,
            handler (oldVal, newVal) {
                if (!newVal || !isEqual(oldVal, newVal)) {
                    this.widget = cloneDeep(this.data)
                    this.getDataWithRefreshInterval(undefined, this.applyPaginationSettings)
                }
            },
        },
        pageSize: {
            handler: function (val) {
                this.storePaginationSettings(val)
            },
        },
        currentPage: {
            immediate: true,
            handler () {
                this.fetchTableData = this.updatePaginatedData(this.tableData)
            }
        },
        tableData: {
            deep: true,
            handler (newV) {
                this.fetchTableData = this.updatePaginatedData(newV)
            }
        }
    },
}
</script>

<style lang="scss" scoped>
::v-deep .el-input__inner::placeholder {
    @apply text-gray-500 font-medium text-base pl-1;
}

::v-deep .el-input__inner {
    @apply text-black font-medium text-base;
}

::v-deep .el-table .el-table__cell {
    padding: 6px 0px;
}

::v-deep .el-table thead th .cell {
    line-height: 15px;
    color: #6e6d6d;
    @apply flex;
}

::v-deep {
    .el-table td, .el-table th {
        .cell {
            @apply text-black font-medium text-sm;
        }
    }
    .el-table td {
        .cell {
            font-weight: 400;
            font-size: var(--dynamic-font-size);
        }
    }

    .el-table th {
        .cell {
            font-size: var(--dynamic-header-font-size);
        }
    }
}

.animation-block {
    height: 350px;
    width: 350px;
    margin: 0 auto;
    text-align: center;
}

.loading {
    display: flex;
    justify-items: center;
    align-items: center;
    background: white;
}
</style>
